/**
 * Filtros de la Vista People (mockup): selects + búsqueda, chips de filtros
 * aplicados con "×" individual y "Limpiar filtros". Todo client-side — las
 * métricas y la tabla se recalculan sobre las filas filtradas.
 */
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const DTALK_FILTER_ALL = '__all__';

export interface DTalkFilterDef {
  k: string;
  label: string;
  options: Array<{ value: string; label: string }>;
}

export function DTalkPeopleFilters({
  search,
  onSearch,
  filters,
  values,
  onSet,
  onClearAll,
}: {
  search: string;
  onSearch: (v: string) => void;
  filters: DTalkFilterDef[];
  values: Record<string, string>;
  onSet: (k: string, v: string) => void;
  onClearAll: () => void;
}) {
  const { t } = useTranslation();
  const applied = Object.entries(values).filter(([, v]) => v !== DTALK_FILTER_ALL);
  const hasAny = applied.length > 0 || search.trim() !== '';

  const defFor = (k: string) => filters.find((f) => f.k === k);
  const chipValue = (k: string, v: string) =>
    defFor(k)?.options.find((o) => o.value === v)?.label ?? v;

  return (
    <Card>
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-bold">Filtros</p>
          {hasAny && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onClearAll}>
              {t('performance.dtalkpeoplefilters.limpiar_filtros')}
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          <Input value={search} onChange={(e) => onSearch(e.target.value)} placeholder={t('performance.dtalkpeoplefilters.buscar_por_nombre_o_email')} className="text-sm" />
          {filters.map((flt) => (
            <select
              key={flt.k}
              value={values[flt.k]}
              onChange={(e) => onSet(flt.k, e.target.value)}
              className="rounded-md border bg-background px-2 py-1.5 text-sm"
            >
              <option value={DTALK_FILTER_ALL}>{t('performance.dtalkpeoplefilters.todos', { label: flt.label })}</option>
              {flt.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ))}
        </div>
        {applied.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="text-muted-foreground">{t('performance.dtalkpeoplefilters.filtros_aplicados')}</span>
            {applied.map(([k, v]) => (
              <button
                key={k}
                className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 hover:bg-muted"
                onClick={() => onSet(k, DTALK_FILTER_ALL)}
                title={t('performance.dtalkpeoplefilters.quitar_filtro')}
              >
                <span className="font-medium">{defFor(k)?.label ?? k}:</span> {chipValue(k, v)}
                <X className="h-3 w-3" />
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
